import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Send, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const statusColor = { pending: "bg-amber-100 text-amber-800", seen: "bg-blue-100 text-blue-800", accepted: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-800" };

export default function Applications() {
  const { user, lang, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    http.get("/applications/mine").then((r) => setItems(r.data));
  }, [user, nav, loading]);

  const messageCompany = async (companyId) => {
    try {
      const { data } = await http.get(`/companies/${companyId}`);
      const owner = data.owner_user_id;
      if (!owner) {
        alert(lang === "fr" ? "Cette entreprise n'a pas encore de propriétaire enregistré pour recevoir vos messages." : "This company has no registered owner to receive messages yet.");
        return;
      }
      nav(`/messages?to=${owner}&company=${companyId}`);
    } catch {
      alert(lang === "fr" ? "Impossible d'ouvrir la messagerie" : "Cannot open messages");
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1E3A8A]">{t(lang, "applications_title")}</h1>
        <p className="text-slate-500 text-sm mt-1">{items.length} {lang === "fr" ? "candidatures envoyées" : "sent"}</p>

        <div className="mt-8 space-y-3">
          {items.map((a) => (
            <div key={a.id} data-testid={`app-${a.id}`} className="rounded-2xl bg-white border border-slate-200 p-5 fade-up">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="font-display font-semibold text-lg">{a.company_name}</p>
                  {a.position_wanted && <p className="text-sm text-slate-600">{a.position_wanted}</p>}
                  <p className="text-xs text-slate-400 mt-1">{new Date(a.created_at).toLocaleDateString()}</p>
                </div>
                <Badge className={`rounded-full ${statusColor[a.status] || ""}`}>{t(lang, `status_${a.status}`)}</Badge>
              </div>
              <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap border-t border-slate-100 pt-3">{a.message}</p>
              <div className="mt-3 flex justify-end">
                <Button
                  data-testid={`app-message-btn-${a.id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => messageCompany(a.company_id)}
                  className="rounded-full text-xs"
                >
                  <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> {lang === "fr" ? "Messagerie" : "Message"}
                </Button>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl">
              <Send className="w-8 h-8 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm mb-4">{t(lang, "no_applications")}</p>
              <Button onClick={() => nav("/map")} className="rounded-full bg-[#1E3A8A] hover:bg-[#172554]">{t(lang, "cta_explore")}</Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
